/**
 * Darstellung für die Einkaufsliste: Mengen, Packungen, Preise und die
 * Reihenfolge der Warengruppen.
 */
import { SHOPPING_CATEGORIES, formatQuantity, packagesNeeded, parseQuantity } from '@cookbook/domain';
import type { ShoppingItem, Store } from '../api/types.ts';
import { parseIngredientLine } from './ingredientParser.ts';

/** Preis in Euro, zum Beispiel „0,99 €“. */
export function formatPrice(cents: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
}

/** Menge eines Eintrags, zum Beispiel „1,5 kg“; leer ohne Menge. */
export function itemAmount(item: Pick<ShoppingItem, 'amount' | 'unit'>): string {
  if (item.amount === null) {
    return '';
  }
  const shown = formatQuantity({ value: item.amount }, item.unit);
  return [shown.amount, shown.unit].filter((part) => part !== '').join(' ');
}

/** Packungsvorschlag mit Preis, zum Beispiel „2 × Mühle 1 kg · 1,98 €“. */
export function packageHint(item: ShoppingItem): { text: string; priceCents: number | null } | null {
  const variant = item.product?.variant;
  if (variant === undefined || variant === null || variant.packageAmount === null) {
    return null;
  }
  const needed = packagesNeeded(
    { amount: item.amount, unit: item.unit },
    { amount: variant.packageAmount, unit: variant.packageUnit, priceCents: variant.priceCents },
    item.name,
  );
  const size = formatQuantity({ value: variant.packageAmount }, variant.packageUnit);
  const label = [variant.brand, variant.label].filter((part) => part !== null && part !== '').join(' ');
  const count = needed?.count ?? 1;
  const price = needed?.priceCents ?? variant.priceCents;
  const text =
    `${count} × ${label === '' ? (item.product?.name ?? item.name) : label} ${size.amount} ${size.unit}`.trim();
  return { text: price === null ? text : `${text} · ${formatPrice(price)}`, priceCents: price };
}

/**
 * Reihenfolge innerhalb einer Warengruppe: Von Hand verschobene Einträge
 * stehen in ihrer Reihenfolge vorn, neue dahinter nach dem Namen.
 */
export function sortWithinGroup<T extends Pick<ShoppingItem, 'name' | 'position'>>(items: readonly T[]): T[] {
  return [...items].sort((a, b) => {
    if (a.position !== null && b.position !== null) {
      return a.position - b.position;
    }
    if (a.position !== null || b.position !== null) {
      return a.position === null ? 1 : -1;
    }
    return a.name.localeCompare(b.name, 'de');
  });
}

/** Reihenfolge der Warengruppen: wie im Laden oder die übliche. */
export function categoryOrder(store: Store | undefined): string[] {
  return store?.aisleOrder ?? SHOPPING_CATEGORIES.map((category) => category.id);
}

/** Liest eine Eingabe wie „2 l Milch“ in Name, Menge und Einheit. */
export function parseQuickAdd(
  text: string,
): { name: string; amount: number | null; unit: string; note: string | null } | null {
  const parsed = parseIngredientLine(text);
  if (parsed === null || parsed.name.trim() === '') {
    return null;
  }
  const quantity = parseQuantity(parsed.amount);
  return {
    name: parsed.name.trim(),
    amount: quantity === null ? null : (quantity.max ?? quantity.value),
    unit: parsed.unit,
    note: parsed.note === '' ? null : parsed.note,
  };
}

/** Liest eine reine Mengenangabe wie „500 g“ oder „2“; leer heißt ohne Menge. */
export function parseAmountWithUnit(text: string): { amount: number | null; unit: string } {
  if (text.trim() === '') {
    return { amount: null, unit: '' };
  }
  // Der Zutatenleser erwartet einen Namen; ein Platzhalter macht die Zeile vollständig.
  const parsed = parseQuickAdd(`${text.trim()} _`);
  return { amount: parsed?.amount ?? null, unit: parsed?.unit ?? '' };
}
